import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, ArrowRight, Sparkles } from "lucide-react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { trackPurchase } from "../lib/marketingPixels";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * PrideSuccess — /pride/success?session_id=cs_xxx
 *
 * Post-checkout celebration screen for the Pride 2026 t-shirt
 * activation. Polls the regular payment-status endpoint (same one
 * `/lock-in/success` uses) until Stripe reports `paid`, then shows
 * the locked-in confirmation.
 *
 * No magic happens here that doesn't happen on /lock-in/success — we
 * just dress it up differently so the QR-scanner sees the moment they
 * spent money on a Pride deal land with Pride visuals.
 */
const PrideSuccess = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState("loading"); // loading | paid | failed
  const [attempts, setAttempts] = useState(0);
  const sessionId =
    new URLSearchParams(window.location.search).get("session_id") || "";

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    if (status === "paid") return;
    if (attempts > 12) {
      // Stripe should have finished by ~30s; bail with a graceful copy.
      setStatus("failed");
      return;
    }
    const t = setTimeout(async () => {
      try {
        // Use the canonical billing/status endpoint — same one
        // /lock-in/success and /billing/success poll. The earlier
        // path `/payments/checkout/status/...` didn't exist on the
        // backend so this loop was 404-looping until it gave up.
        const { data } = await axios.get(
          `${API}/billing/status/${sessionId}`,
          { withCredentials: true },
        );
        if (data?.payment_status === "paid") {
          // TikTok/Taboola/GTM CompletePayment — $49.99 Pride 2026
          // annual subscription (same lifetime grandfathering as
          // /lock-in but no 100-cap).
          const amountUsd =
            typeof data?.amount === "number" && data.amount > 0
              ? data.amount
              : 49.99;
          trackPurchase({
            value: amountUsd,
            currency: (data?.currency || "USD").toUpperCase(),
            content_id: "pride_2026",
            content_type: "subscription",
            transaction_id: sessionId,
          });
          setStatus("paid");
          return;
        }
      } catch {
        // network blip — just keep polling
      }
      setAttempts((n) => n + 1);
    }, 2500);
    return () => clearTimeout(t);
  }, [attempts, sessionId, status]);

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      data-testid="pride-success"
    >
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#A1D5F5] via-[#FBE7EE] to-white" />
      <div className="absolute inset-0 -z-10 opacity-50 bg-[radial-gradient(ellipse_at_top_right,_#F5B6CC,_transparent_60%)]" />

      <div className="max-w-md mx-auto px-5 pt-20 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {status === "loading" && (
            <div className="text-center">
              <div className="text-5xl mb-4">🏳️‍⚧️</div>
              <h1 className="font-serif italic text-2xl text-[#2C2123] mb-3">
                {t("prideSuccess.loadingTitle")}
              </h1>
              <p className="text-[#5C4B51] text-sm">
                {t("prideSuccess.loadingBody")}
              </p>
            </div>
          )}

          {status === "paid" && (
            <div>
              <div className="text-6xl mb-5 text-center">💋🏳️‍⚧️</div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#9B6A6C] mb-3 text-center">
                {t("prideSuccess.paidKicker")}
              </p>
              <h1 className="font-serif italic text-3xl text-[#2C2123] mb-4 text-center leading-tight">
                {t("prideSuccess.paidHeadlineLine1")}
                <br />
                <span className="text-[#9B6A6C]">{t("prideSuccess.paidHeadlineLine2")}</span>
              </h1>
              <p className="text-[#3C2F33] text-base leading-relaxed mb-6 text-center">
                {t("prideSuccess.paidBody")}
              </p>

              <div
                className="bg-white/85 backdrop-blur-sm border border-[#EADDD7] rounded-2xl p-5 mb-5"
                data-testid="pride-next-steps"
              >
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#9B6A6C] mb-2">
                  {t("prideSuccess.nextStepsKicker")}
                </p>
                <ul className="space-y-2 text-sm text-[#3C2F33]">
                  <li className="flex gap-2 items-start">
                    <Heart className="h-4 w-4 text-[#D5A8A4] flex-shrink-0 mt-0.5 fill-[#F5B6CC]" />
                    {t("prideSuccess.nextStep1")}
                  </li>
                  <li className="flex gap-2 items-start">
                    <Heart className="h-4 w-4 text-[#D5A8A4] flex-shrink-0 mt-0.5 fill-[#F5B6CC]" />
                    {t("prideSuccess.nextStep2")}
                  </li>
                  <li className="flex gap-2 items-start">
                    <Heart className="h-4 w-4 text-[#D5A8A4] flex-shrink-0 mt-0.5 fill-[#F5B6CC]" />
                    {t("prideSuccess.nextStep3")}
                  </li>
                </ul>
              </div>

              <Link
                to="/"
                className="w-full bg-[#9B6A6C] hover:bg-[#5D4050] text-white px-6 py-3.5 rounded-full font-medium text-base transition-colors inline-flex items-center justify-center gap-2 shadow-[0_10px_24px_rgba(155,106,108,0.32)]"
                data-testid="pride-success-start-btn"
              >
                <Sparkles className="h-4 w-4" />
                {t("prideSuccess.startCta")}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}

          {status === "failed" && (
            <div className="text-center">
              <div className="text-4xl mb-4">🏳️‍⚧️</div>
              <h1 className="font-serif italic text-2xl text-[#2C2123] mb-3">
                {t("prideSuccess.failedTitle")}
              </h1>
              <p className="text-[#5C4B51] text-sm mb-5 leading-relaxed">
                {t("prideSuccess.failedBody")}
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-[#9B6A6C] hover:text-[#5D4050]"
              >
                {t("prideSuccess.failedHome")} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PrideSuccess;
